// src/pages/TutorialPage.js
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, Badge, Button, Alert } from "react-bootstrap";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";

const StepCard = ({ number, icon, title, color = "#0d6efd", children }) => (
  <Card className="mb-3 border-0 shadow-sm">
    <Card.Body className="d-flex align-items-start gap-3">
      <div
        className="rounded-circle text-white d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 36, height: 36, fontSize: "1rem", fontWeight: "bold", backgroundColor: color }}
      >
        {number}
      </div>
      <div className="flex-grow-1">
        <h6 className="fw-bold mb-2">{icon} {title}</h6>
        <div className="text-muted" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
          {children}
        </div>
      </div>
    </Card.Body>
  </Card>
);

export default function TutorialPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const sectionWeeklyRef = useRef(null);
  const sectionCalendarRef = useRef(null);
  const sectionInstallRef = useRef(null);

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Container className="py-4" style={{ maxWidth: 720 }}>
      {/* Header */}
      <Card className="mb-4 border-0 shadow-sm bg-primary text-white">
        <Card.Body className="text-center py-4">
          <h2 className="fw-bold mb-2">{t(language, "tutorialTitle")}</h2>
          <p className="mb-0 opacity-75">{t(language, "tutorialSubtitle")}</p>
        </Card.Body>
      </Card>

      {/* Quick Navigation */}
      <div className="d-flex justify-content-center flex-wrap gap-3 mb-4">
        <Badge
          bg="primary"
          className="px-3 py-2"
          style={{ fontSize: "0.95rem", cursor: "pointer" }}
          onClick={() => scrollTo(sectionWeeklyRef)}
        >
          {t(language, "tutorialWeeklyReport")}
        </Badge>
        <Badge
          bg="success"
          className="px-3 py-2"
          style={{ fontSize: "0.95rem", cursor: "pointer" }}
          onClick={() => scrollTo(sectionCalendarRef)}
        >
          {t(language, "tutorialCalendar")}
        </Badge>
        <Badge
          bg="dark"
          className="px-3 py-2"
          style={{ fontSize: "0.95rem", cursor: "pointer" }}
          onClick={() => scrollTo(sectionInstallRef)}
        >
          {t(language, "tutorialInstallApp")}
        </Badge>
      </div>

      {/* ==================== SECAO 1: FECHAMENTO SEMANAL ==================== */}
      <div ref={sectionWeeklyRef} className="mb-2">
        <h4 className="fw-bold mb-3">
          <span className="me-2">📋</span>
          {t(language, "tutorialWeeklyReport")}
        </h4>
      </div>

      <StepCard number={1} icon="⬅️➡️" title={t(language, "tutorialNavWeeks")}>
        <p className="mb-0">{t(language, "tutorialNavWeeksDesc")}</p>
      </StepCard>

      <StepCard number={2} icon="🔍" title={t(language, "tutorialAddClients")}>
        <p className="mb-2">{t(language, "tutorialAddClientsDesc")}</p>
        <Alert variant="info" className="mb-0 py-2 px-3" style={{ fontSize: "0.9rem" }}>
          {t(language, "tutorialTip")}: {t(language, "tutorialAddClientsManual")}
        </Alert>
      </StepCard>

      <StepCard number={3} icon="✏️" title={t(language, "tutorialEditHours")}>
        <p className="mb-2">{t(language, "tutorialEditHoursDesc")}</p>
        <Alert variant="light" className="mb-0 py-2 px-3 border" style={{ fontSize: "0.9rem" }}>
          {t(language, "tutorialEditHoursColors")}
        </Alert>
      </StepCard>

      <StepCard number={4} icon="⚠️" title={t(language, "tutorialJustification")}>
        <p className="mb-0">{t(language, "tutorialJustificationDesc")}</p>
      </StepCard>

      <StepCard number={5} icon="👥" title={t(language, "tutorialTeamSize")}>
        <p className="mb-0">{t(language, "tutorialTeamSizeDesc")}</p>
      </StepCard>

      <StepCard number={6} icon="💾" title={t(language, "tutorialSave")}>
        <p className="mb-2">{t(language, "tutorialSaveDesc")}</p>
        <Alert variant="warning" className="mb-0 py-2 px-3" style={{ fontSize: "0.9rem" }}>
          {t(language, "tutorialImportant")}: {t(language, "tutorialSaveWarning")}
        </Alert>
      </StepCard>

      <StepCard number={7} icon="📊" title={t(language, "tutorialSummary")}>
        <ul className="mb-0 ps-3" style={{ listStyleType: "none" }}>
          <li className="mb-1">📌 {t(language, "tutorialSummaryWork")}</li>
          <li className="mb-1">🍽️ {t(language, "tutorialSummaryBreaks")}</li>
          <li className="mb-1">🚗 {t(language, "tutorialSummaryTravel")}</li>
          <li>✅ {t(language, "tutorialSummaryTotal")}</li>
        </ul>
      </StepCard>

      {/* ==================== SECAO 2: CALENDARIO ==================== */}
      <div ref={sectionCalendarRef} className="mb-2 mt-5">
        <h4 className="fw-bold mb-3">
          <span className="me-2">📅</span>
          {t(language, "tutorialCalendar")}
        </h4>
      </div>

      <StepCard number={1} icon="👀" title={t(language, "tutorialViewJobs")} color="#198754">
        <p className="mb-0">{t(language, "tutorialViewJobsDesc")}</p>
      </StepCard>

      <StepCard number={2} icon="👆" title={t(language, "tutorialJobDetails")} color="#198754">
        <p className="mb-2">{t(language, "tutorialJobDetailsDesc")}</p>
        <ul className="mb-0 ps-3">
          <li className="mb-1">🕐 {t(language, "tutorialJobTime")}</li>
          <li className="mb-1">📍 {t(language, "tutorialJobAddress")}</li>
          <li className="mb-1">✅ {t(language, "tutorialJobChecklist")}</li>
          <li className="mb-1">📝 {t(language, "tutorialJobNotes")}</li>
          <li>🔑 {t(language, "tutorialJobPassword")}</li>
        </ul>
      </StepCard>

      <StepCard number={3} icon="📸" title={t(language, "tutorialUploadPhotos")} color="#198754">
        <p className="mb-2">{t(language, "tutorialUploadPhotosDesc")}</p>
        <ol className="mb-2 ps-3">
          <li className="mb-1">{t(language, "tutorialUploadStep1")}</li>
          <li className="mb-1">{t(language, "tutorialUploadStep2")}</li>
          <li className="mb-1">{t(language, "tutorialUploadStep3")}</li>
          <li>{t(language, "tutorialUploadStep4")}</li>
        </ol>
        <Alert variant="info" className="mb-0 py-2 px-3" style={{ fontSize: "0.9rem" }}>
          {t(language, "tutorialTip")}: {t(language, "tutorialUploadTip")}
        </Alert>
      </StepCard>

      <StepCard number={4} icon="🖼️" title={t(language, "tutorialViewGallery")} color="#198754">
        <p className="mb-0">{t(language, "tutorialViewGalleryDesc")}</p>
      </StepCard>

      {/* ==================== SECAO 3: INSTALAR NO CELULAR ==================== */}
      <div ref={sectionInstallRef} className="mb-2 mt-5">
        <h4 className="fw-bold mb-3">
          <span className="me-2">📱</span>
          {t(language, "tutorialInstallApp")}
        </h4>
      </div>

      <p className="text-muted mb-3" style={{ fontSize: "0.95rem" }}>
        {t(language, "tutorialInstallDesc")}
      </p>

      <StepCard number={"A"} icon="🤖" title={t(language, "tutorialAndroid")} color="#343a40">
        <ol className="mb-0 ps-3">
          <li className="mb-1">{t(language, "tutorialAndroidStep1")}</li>
          <li className="mb-1">{t(language, "tutorialAndroidStep2")}</li>
          <li className="mb-1">{t(language, "tutorialAndroidStep3")}</li>
          <li className="mb-1">{t(language, "tutorialAndroidStep4")}</li>
          <li>{t(language, "tutorialAndroidStep5")}</li>
        </ol>
      </StepCard>

      <StepCard number={"i"} icon="🍎" title={t(language, "tutorialIphone")} color="#343a40">
        <ol className="mb-2 ps-3">
          <li className="mb-1">{t(language, "tutorialIphoneStep1")}</li>
          <li className="mb-1">{t(language, "tutorialIphoneStep2")}</li>
          <li className="mb-1">{t(language, "tutorialIphoneStep3")}</li>
          <li className="mb-1">{t(language, "tutorialIphoneStep4")}</li>
          <li>{t(language, "tutorialIphoneStep5")}</li>
        </ol>
        <Alert variant="warning" className="mb-0 py-2 px-3" style={{ fontSize: "0.9rem" }}>
          {t(language, "tutorialImportant")}: {t(language, "tutorialIphoneWarning")}
        </Alert>
      </StepCard>

      {/* Back Button */}
      <div className="text-center mt-4 mb-3">
        <Button variant="secondary" onClick={() => navigate("/")}>
          ← {t(language, "back")}
        </Button>
      </div>
    </Container>
  );
}
